// CallModalStack.tsx
import { Drawer } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import CallerIdCard from './CallerIdCard';
import { ICallerIdCustomer } from './type';
import { chunk } from 'lodash';

type CallItem = {
  callId: string;
  customer: ICallerIdCustomer | null;
  createdAt: number;
  isClosing?: boolean;
};

export type CallerIdApi = {
  open: (call: { callId: string; customer: ICallerIdCustomer | null; }) => void;
  close: (callId: string) => void;
  update: (call: { callId: string; customer?: ICallerIdCustomer | null; }) => void;
};

let apiRef: CallerIdApi | null = null;

export function CallModalStack() {
  const [calls, setCalls] = useState<CallItem[]>([]);

  const open = useCallback((args: { callId: string; customer: ICallerIdCustomer | null; }) => {
    setCalls((prev) => {
      // nếu trùng callId thì update thay vì tạo modal mới
      const idx = prev.findIndex((c) => c.callId === args.callId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], customer: args.customer };
        return next;
      }
      return [{ callId: args.callId, customer: args.customer, createdAt: Date.now() }, ...prev];
    });
  }, []);

  const close = useCallback((callId: string) => {
    // Mark item as closing to trigger animation
    setCalls((prev) =>
      prev.map((c) => (c.callId === callId ? { ...c, isClosing: true } : c))
    );
  }, []);

  const removeCall = useCallback((callId: string) => {
    // Actually remove the item after animation
    setCalls((prev) => prev.filter((c) => c.callId !== callId));
  }, []);

  const update = useCallback((args: { callId: string; customer?: ICallerIdCustomer | null; }) => {
    setCalls((prev) =>
      prev.map((c) => (c.callId === args.callId ? { ...c, customer: args.customer ?? c.customer } : c))
    );
  }, []);

  // expose singleton API
  useMemo(() => {
    apiRef = { open, close, update };
    return null;
  }, [open, close, update]);

  const callers = useMemo(() => {
    return chunk(calls, 3).reverse();
  }, [calls]);

  return (
    <Drawer
      open={calls.length > 0}
      footer={null}
      closable={false}
      width={'auto'}
      destroyOnClose
      mask
      placement="right"
      bodyStyle={{ padding: 0, background: 'transparent', boxShadow: 'none' }}
      contentWrapperStyle={{ boxShadow: 'none', background: 'transparent' }}
      style={{ background: 'transparent' }}
    >
      <div style={{ display: 'flex', flexDirection: 'row', padding: 12, gap: 16 }}>
        {callers.map((row, i) => (
          <div key={`row_${i}`} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {row.map(call => (
              <div
                key={call.callId}
                className={call.isClosing ? 'caller-card-wrapper closing' : 'caller-card-wrapper'}
                onAnimationEnd={() => {
                  if (call.isClosing) {
                    removeCall(call.callId);
                  }
                }}
              >
                <CallerIdCard
                  customer={call.customer}
                  onClose={() => close(call.callId)}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
      <style>{`
        .caller-card-wrapper.closing {
          animation: fadeOut 0.3s ease-in forwards;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
      `}</style>
    </Drawer>
  );
}

const CallerId = {
  open: (args: { callId: string; customer: ICallerIdCustomer | null; }) => apiRef?.open(args),
  close: (callId: string) => apiRef?.close(callId),
  update: (args: { callId: string; customer?: ICallerIdCustomer | null; }) => apiRef?.update(args),
};

export default CallerId;
